import { useEffect, useState } from 'react';
import { RichText } from 'prismic-reactjs';
import { AddToShoppingCartSource } from '@monofront/types';
import { CarouselProductsV2 as CarouselProductsComponent } from '@components/CarouselProducts';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import * as prismicR from '@prismicio/richtext';
import logger from '@utils/logger';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import { useApolloClient } from '@apollo/client';
import { GET_MERCH_RECOMMENDATIONS } from '@monofront/business/queries';
import MerchRecommendationsCarouselV2 from '@components/MerchRecommendationsCarousel/MerchRecommendationsCarouselV2';
import { IMultipleProductsCarousels } from './types';
import { useEligibility } from '@monofront/business/contexts';

const MultipleProductsCarouselTitle = ({ title, titleIcon }) => {
  return (
    <div className="tw-mb-6 tw-flex tw-items-baseline">
      <RichText
        render={title}
        htmlSerializer={CustomPrismicHeading({
          className: 'tw-lf-title-lg',
        })}
      />
      {titleIcon?.url && (
        <span className="title-icon">
          <ResponsiveImage
            width={titleIcon.dimensions.width}
            height={titleIcon.dimensions.height}
            src={titleIcon.url}
            alt={titleIcon.alt}
          />
        </span>
      )}
    </div>
  );
};

const MultipleProductsCarouselCollectionButton = ({
  handle,
  label,
  active,
  onClick,
}: {
  handle?: string;
  label: string;
  active: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement | HTMLLIElement>) => void;
}) => {
  if (handle) {
    return (
      <li>
        <a
          href={`/collections/${handle}`}
          className={`tw-lf-body-regular-sm tw-lf-chip tw-lf-chip-suggestion ${
            active ? ' tw-lf-chip-primary' : ''
          }`}
          onClick={onClick}
        >
          {label}
        </a>
      </li>
    );
  }

  return (
    <li
      className={`tw-lf-body-regular-sm tw-lf-chip tw-lf-chip-suggestion ${
        active ? ' tw-lf-chip-primary' : ''
      }`}
      onClick={onClick}
    >
      {label}
    </li>
  );
};

const MultipleProductsCarouselsWithRecommendations: React.FC<
  IMultipleProductsCarousels & { displayedCarouselIndex: number }
> = ({ items, displayedCarouselIndex, isViewItemListEnabled }) => {
  const apolloClient = useApolloClient();
  const { shoppingCart } = useShoppingCart();
  const { eligibility } = useEligibility();

  useEffect(() => {
    // Preload recommendations of hidden carousels. Use setTimeout to make it lazy.
    const timeout = setTimeout(() => {
      items?.forEach((carousel, index) => {
        if (!carousel.recoWidgetId || index === displayedCarouselIndex) {
          return;
        }

        void apolloClient.query({
          query: GET_MERCH_RECOMMENDATIONS,
          variables: {
            widgetId: carousel.recoWidgetId,
            variables: {
              cart: {
                skus:
                  shoppingCart.products?.map((product) => product.sku) || [],
              },
              url: window.location.href,
              shouldIncludeFreshProducts: Boolean(eligibility.ultraFresh),
            },
          },
          fetchPolicy: 'cache-first',
        });
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [items]);

  if (!items || !items.length) return null;

  const carousel = items[displayedCarouselIndex];

  return (
    <>
      {carousel.recoWidgetId && (
        <MerchRecommendationsCarouselV2
          showTitle={false}
          widgetId={carousel.recoWidgetId}
          collection={carousel.collectionLink}
          headerTitlePlainText={
            carousel.title && prismicR.asText(carousel.title)
          }
          trackingId={carousel.trackingId || undefined}
          containerContext={{
            source: AddToShoppingCartSource.CAROUSEL,
            widgetName: carousel.trackingId || undefined,
          }}
          isViewItemListEnabled={isViewItemListEnabled}
        />
      )}

      {!carousel.recoWidgetId && carousel.algoliaProducts?.length && (
        <CarouselProductsComponent
          collection={carousel.collectionLink}
          headerTitlePlainText={
            carousel.title && prismicR.asText(carousel.title)
          }
          products={carousel.algoliaProducts}
          trackingId={carousel.trackingId || undefined}
          containerContext={{
            source: AddToShoppingCartSource.CAROUSEL,
            widgetName: carousel.trackingId || undefined,
          }}
        />
      )}
    </>
  );
};

const MultipleProductsCarousels: React.FC<IMultipleProductsCarousels> = ({
  items,
  title,
  titleIcon,
  isViewItemListEnabled,
}) => {
  const [displayedCarouselIndex, setDisplayedCarouselIndex] = useState(0);

  let needShoppingCart = false;

  for (const carousel of items) {
    if (carousel.recoWidgetId) {
      needShoppingCart = true;

      break;
    }
  }

  useEffect(() => {
    // Preload images of hidden carousels. Use setTimeout to make it lazy.
    const timeout = setTimeout(() => {
      items?.forEach((carousel, index) => {
        if (index === displayedCarouselIndex) {
          return;
        }

        carousel.algoliaProducts?.forEach((product) => {
          if (product?.images?.length) {
            const image = product.images[0];
            const img = new Image();

            img.src = `${image}?width=312&quality=60`;
            img.decode();
          }
        });
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [items]);

  if (!items || !items.length) return null;

  const carousel = items[displayedCarouselIndex];

  return (
    <>
      <section className="multiple-carousel-products">
        <MultipleProductsCarouselTitle title={title} titleIcon={titleIcon} />

        <div>
          <ul className="tw-mb-4 tw-flex tw-gap-2 tw-overflow-x-auto">
            {items.map(({ menuItem, collection }, index) => {
              if (!menuItem) {
                logger.error(
                  'Missing multi carousels item label or collection',
                  { menuItem, collection, index },
                );

                return null;
              }

              return (
                <MultipleProductsCarouselCollectionButton
                  key={index}
                  handle={collection?.handle}
                  label={menuItem}
                  active={displayedCarouselIndex === index}
                  onClick={(e) => {
                    e.preventDefault();
                    setDisplayedCarouselIndex(index);
                  }}
                />
              );
            })}
          </ul>
        </div>

        {needShoppingCart && (
          <MultipleProductsCarouselsWithRecommendations
            key={`recommendation_carousel_index_${displayedCarouselIndex}`}
            items={items}
            displayedCarouselIndex={displayedCarouselIndex}
            isViewItemListEnabled={isViewItemListEnabled}
          />
        )}

        {!needShoppingCart && carousel.algoliaProducts?.length && (
          <CarouselProductsComponent
            key={`carousel_index_${displayedCarouselIndex}`}
            collection={carousel.collectionLink}
            headerTitlePlainText={
              (Array.isArray(carousel.title) &&
                prismicR.asText(carousel.title)) ||
              (carousel.title as unknown as string)
            }
            products={carousel.algoliaProducts}
            trackingId={carousel.trackingId || undefined}
            containerContext={{
              source: AddToShoppingCartSource.CAROUSEL,
              widgetName: carousel.trackingId || undefined,
            }}
          />
        )}
      </section>
      <style jsx>{`
        @media (max-width: 800px) {
          .multiple-carousel-products :global(.swiper) {
            margin: var(--negative-padding-layout-base);
            padding: var(--padding-layout-base);
            width: 100vw;
          }
        }
      `}</style>
    </>
  );
};

export default MultipleProductsCarousels;
